import React, { useCallback, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { theme } from 'styled-tools'
import ImageOption from '../Options/ImageOption'
import BoxedImageTextOption from '../Options/BoxedImageTextOption'
import TextOption from '../Options/TextOption'
import TextBoxedOption from '../Options/TextBoxedOption'
import settings from '../../../components/settings'

const OptionsItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin: ${theme('questionBlock.options.margin.mobile')};
  width: 100%;

  ${(props) =>
    props.hasTextBoxedOption &&
    `
    flex-direction: row;
    flex-wrap: wrap;
  `}
  ${(props) =>
    props.hasTextBoxedOption &&
    props.theme.media.max.sm`
    justify-content: space-between;
  `}
    ${(props) =>
    props.hasImageOption &&
    settings.brand === 'magnet' &&
    props.theme.media.max.sm`
    grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  `}
    ${(props) =>
    props.hasImageOption &&
    settings.brand === 'magnet' &&
    `
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    @media (max-width: 428px) {
      grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
    }
  `}

  ${(props) => props.theme.media.min.md`
    min-width: 580px;
  `};

  ${(props) => props.theme.media.min.sm`

    margin: ${theme('questionBlock.options.margin.desktop')};
    flex-wrap: wrap;
    flex-direction: row;
  `};
`

const OptionsItem = styled.div`
  cursor: pointer;
  position: relative;
  border: ${theme('questionBlock.optionsItem.border')};

  border: ${theme('questionBlock.optionsItem.borderNormal')};
  background-color: ${theme('questionBlock.optionsItem.backgroundNormal')};

  border-radius: ${theme('questionBlock.optionsItem.borderRadius')};
  margin-bottom: 10px;
  white-space: nowrap;

  text-align: ${(p) => (p.type === 'TextOption' ? 'center' : 'left')};

  ${(props) =>
    props.hasImageOption &&
    settings.brand === 'magnet' &&
    `
    margin: auto !important;
    margin-bottom: 10px !important;
  `}

  ${(props) =>
    props.hasTextBoxedOption &&
    props.theme.media.max.sm`
    flex: 0 0 48%;
    margin-right: 0px !important;
  `}

  ${(props) => props.theme.media.min.sm`
      margin: ${
        props['data-epiid'] === 8854 || props.type === 'ImageModalOption'
          ? '0px 0px 25px 0px'
          : theme('questionBlock.optionsItem.margin.desktop')
      };
    `}

  ${(props) =>
    props.type === 'ImageOption' &&
    props.theme.media.min.sm`
        max-width: ${
          props['data-epiid'] === 8854
            ? '100%'
            : theme('questionBlock.optionsItem.width.desktop')
        };
        width: ${props['data-epiid'] === 8854 ? '100%' : 'auto'};
    `}
    ${(props) =>
    props.type === 'ImageModalOption' &&
    props.theme.media.min.sm`
        max-width: 100%;
        width: 100%;
    `}

  ${(props) =>
    props.active &&
    css`
      border: ${theme('questionBlock.optionsItem.activeBorder')};
      box-shadow: ${theme('questionBlock.optionsItem.activeBoxShadow')};

      background-color: ${theme('questionBlock.optionsItem.backgroundActive')};
      border: ${theme('questionBlock.optionsItem.borderActive')};
    `}
`

const AnotherOption = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  font-weight: 400;
  width: 100%;
  margin: ${theme('questionBlock.options.margin.mobile')};
  ${(props) => props.theme.media.min.sm`
    margin:0;
    padding: ${theme('questionBlock.anotherOption.padding', '14px')(props)};
    box-sizing: border-box;
  `}
  ${(props) => props.theme.media.min.lg`
    width: auto;
    min-width: ${theme('questionBlock.anotherOption.minWidth', '160px')(props)};
    justify-content: flex-start;
    justify-content: ${theme('questionBlock.anotherOption.justifyContent')};
  `}
`

const AnotherOptionText = styled.p`
  cursor: pointer;
  color: ${theme('colors.darkSlate')};
  font-weight: ${(props) =>
    theme('questionBlock.anotherOption.text.fontSize')(props) || '700'};
  font-size: ${theme('questionBlock.anotherOption.text.fontSize')};
  text-align: center;
  display: block;
  margin: 0px auto;
  font-family: ${theme('typography.paragraph.fontFamily')};
  ${(props) => props.theme.media.min.sm`
      margin: 0px;
      text-align: right;
      display:inline;
  `}

  ${(props) =>
    props.active &&
    `
    color: ${props.theme.colors.link};
  `}
`

const OptionsContainer = styled.div`
  display: ${theme('options.container.display')};
  flex-direction: ${theme('options.container.flexDirection')};
  justify-content: ${theme('options.container.justifyContent')};
  flex-basis: ${(props) =>
    theme('questionBlock.options.container.flexBasis')(props) || '70%'};
  ${(props) =>
    props.active &&
    `
    color: ${props.theme.colors.link};
  `}
  ${(props) =>
    props.hasTextBoxedOption &&
    `
    display: flex;
    align-items: center;
  `}
`

export const SelectQuestion = ({ question, answers, onChange }) => {
  const hasTextOption = useMemo(
    () => question.options?.some((o) => o.contentType === 'TextBoxedOption'),
    [question.options]
  )

  const hasImageOption = useMemo(
    () => question.options?.some((o) => o.contentType === 'ImageOption'),
    [question.options]
  )

  const style = useMemo(
    () => (question.questionStyle || 'style-1').replace('-', ''),
    [question.questionStyle]
  )

  const isSelected = useCallback((v) => answers.includes(v), [answers])

  const onSingleClick = useCallback(
    (value) => {
      const selected = answers.indexOf(value)
      if (selected === -1) {
        onChange([value])
      } else {
        onChange([])
      }
    },
    [answers, onChange]
  )

  const onMultiClick = useCallback(
    (value) => {
      const selected = answers.indexOf(value)
      if (selected === -1) {
        onChange([...answers, value])
      } else {
        onChange([
          ...answers.slice(0, selected),
          ...answers.slice(selected + 1),
        ])
      }
    },
    [answers, onChange]
  )

  if (!question.options?.length) {
    return null
  }

  return (
    <OptionsContainer hasTextBoxedOption={hasTextOption}>
      <OptionsItemWrapper
        repeat={question.options.length}
        hasTextBoxedOption={hasTextOption}
        hasImageOption={hasImageOption}
      >
        {question.options.map((o) => (
          <OptionsItem
            data-track={`option-${o.name}`.toLowerCase()}
            key={o.name}
            type={o.contentType}
            active={isSelected(o.name)}
            data-selected={isSelected(o.name) ? 'true' : 'false'}
            hasTextBoxedOption={hasTextOption}
            hasImageOption={hasImageOption}
            onClick={() =>
              question.optionType === 'single'
                ? onSingleClick(o.name)
                : onMultiClick(o.name)
            }
          >
            {o.contentType === 'ImageOption' && (
              <ImageOption
                name={o.name}
                image={o.image}
                text={o.text}
                active={isSelected(o.name)}
                questionStyle={style}
              />
            )}
            {o.contentType === 'TextOption' && (
              <TextOption text={o.firstText} active={isSelected(o.name)} />
            )}
            {o.contentType === 'TextBoxedOption' && (
              <TextBoxedOption text={o.firstText} active={isSelected(o.name)} />
            )}
            {o.contentType === 'ImageModalOption' && (
              <BoxedImageTextOption
                name={o.name}
                image={o.image}
                text={o.text}
                modalDescription={o.modalDescription}
                modalHeading={o.modalHeading}
                modalImages={o.modalImages}
                active={isSelected(o.name)}
                questionStyle={style}
              />
            )}
          </OptionsItem>
        ))}
      </OptionsItemWrapper>
      {question.otherAnswerText && (
        <AnotherOption data-track={`not-sure-${question.sortOrder}`}>
          <AnotherOptionText
            active={isSelected(question.otherAnswerText)}
            onClick={() => onSingleClick(question.otherAnswerText)}
          >
            {question.otherAnswerText}
          </AnotherOptionText>
        </AnotherOption>
      )}
    </OptionsContainer>
  )
}
